import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import Loading from "../../../../common-components/Loading";
import {
  getIndirectVersionsAPI,
  getIndirectLaborMonthsAPI,
  getIndirectLaborComparisonAPI,
  downloadLaborComparisonAPI,
} from "../../../../api/reports-api";

import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ACCENT_COLOR,
  TABLE_BORDER,
  CELL_FG_MUTED,
  CELL_FG_STRONG,
  TH_BG_BAND,
  TH_TEXT,
  TABLE_SCROLL_WRAP_STYLE,
} from "../../reportTableConfig";
import { PL_POS_C, PL_NEG_C } from "../pl-by-program/plByProgramTheme";

/* ══════════════════════════════════════════════════════════════ */
/*  Color constants                                               */
/* ══════════════════════════════════════════════════════════════ */
const NAV = "#0D2137";
const HEADER_BG = TH_BG;
const HEADER_COL = TH_BG;

/* ══════════════════════════════════════════════════════════════ */
/*  Formatter                                                     */
/* ══════════════════════════════════════════════════════════════ */
function fmtI(v) {
  if (v === null || v === undefined) return "";
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  svbFilters: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  svbFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  svbFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  svbFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 110,
    height: "100%",
    fontFamily: "'IBM Plex Sans', sans-serif",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
  thSvbType: {
    background: HEADER_BG,
    color: TH_TEXT,
    padding: "7px 14px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderRight: "1px solid #4a4a4c",
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 4,
    minWidth: "200px",
  },
  thSvbBand: {
    background: HEADER_COL,
    color: TH_TEXT,
    padding: "6px 10px",
    fontWeight: 700,
    fontSize: "11px",
    textAlign: "center",
    borderLeft: "1px solid #4a4a4c",
    borderBottom: "1px solid #4a4a4c",
    position: "sticky",
    top: 0,
    zIndex: 4,
  },
  thSvbCol: {
    background: HEADER_BG,
    color: TH_TEXT,
    padding: "5px 8px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderRight: "1px solid #4a4a4c",
    minWidth: "95px",
    position: "sticky",
    top: "30px",
    zIndex: 3,
  },
  thSvbColFirst: {
    background: HEADER_BG,
    color: TH_TEXT,
    padding: "5px 8px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderLeft: "1px solid #4a4a4c",
    borderRight: "1px solid #4a4a4c",
    minWidth: "95px",
    position: "sticky",
    top: "30px",
    zIndex: 3,
  },
  thSvbVar: {
    background: HEADER_BG,
    color: TH_TEXT,
    padding: "5px 8px",
    fontWeight: 600,
    fontSize: "10px",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderRight: "1px solid #4a4a4c",
    minWidth: "95px",
    position: "sticky",
    top: "30px",
    zIndex: 3,
  },
  tdSvbType: {
    padding: "5px 14px",
    fontWeight: 500,
    color: "#1e293b",
    background: "#fff",
    borderBottom: "1px solid #F1F4F8",
    borderRight: "1px solid #E2E8F0",
    fontSize: "11px",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdSvbVal: {
    padding: "5px 8px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #F1F4F8",
    borderRight: "1px solid #F4F6F9",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  tdSvbValFirst: {
    padding: "5px 8px",
    textAlign: "right",
    color: "#334155",
    background: "#fff",
    borderBottom: "1px solid #F1F4F8",
    borderLeft: "2px solid #D8E4EE",
    borderRight: "1px solid #F4F6F9",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  tdSvbTotalType: {
    padding: "6px 14px",
    fontWeight: 700,
    color: "#fff",
    background: NAV,
    borderTop: "2px solid #0a1e30",
    fontSize: "11px",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  tdSvbTotalVal: {
    padding: "6px 8px",
    textAlign: "right",
    fontWeight: 700,
    color: "#fff",
    background: NAV,
    borderTop: "2px solid #0a1e30",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  tdSvbTotalValFirst: {
    padding: "6px 8px",
    textAlign: "right",
    fontWeight: 700,
    color: "#fff",
    background: NAV,
    borderTop: "2px solid #0a1e30",
    borderLeft: "2px solid rgba(255,255,255,.15)",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Export default                                                */
/* ══════════════════════════════════════════════════════════════ */
/* ── Inline table styles (Employee Labor / IndirectPL pattern) ── */
const FONT_ELC = "'IBM Plex Sans', sans-serif";

const TOTAL_FG = CELL_FG_STRONG;

const GROUP_BG = "#EEF4FF";

const ELC_COLS = [
  { key: "costType", label: "Cost Type", w: 180, left: 0 },
  { key: "empName", label: "Employee Name", w: 220, left: 180 },
  { key: "empId", label: "Employee ID", w: 140, left: 400 },
];
/* Maps ELC_COLS.key → actual row field used for sorting */
const ELC_DETAIL_KEY = {
  costType: "costType",
  empName: "employeeName",
  empId: "employeeId",
};
const ELC_FIXED_W = 555;
const ELC_COL_W = 130;
const TYPE_W_ELC =
  ELC_FIXED_W; /* match detail table so MTD/YTD/FY columns align */
const Z_H = 6,
  Z_S = 4,
  GH_ELC = 36,
  CH_ELC = 38,
  RH_ELC = 44;
const TH_SH_G_ELC = "inset 0 -1px 0 #4a4a4c";
const TH_SH_ELC = "inset 0 -1px 0 #4a4a4c";

const thCoreELC = (top, h, z, pad, bg) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: TH_TEXT,
  verticalAlign: "middle",
  padding: pad,
  whiteSpace: "nowrap",
  border: "none",
  borderRight: "1px solid #4a4a4c",
});
const thFixedSpanELC = (col, isLast) => ({
  ...thCoreELC(0, GH_ELC + CH_ELC, Z_H + 1, "7px 10px", HEADER_BG),
  left: col.left,
  width: col.w,
  minWidth: col.w,
  maxWidth: col.w,
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "left",
  boxShadow: isLast
    ? `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_ELC}`
    : TH_SH_ELC,
});
const thBandELC = () => ({
  ...thCoreELC(0, GH_ELC, Z_H, "7px 12px", HEADER_BG),
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "center",
  boxShadow: TH_SH_G_ELC,
});
const thSubELC = () => ({
  ...thCoreELC(GH_ELC, CH_ELC, Z_H, "6px 10px", HEADER_COL),
  fontSize: 10,
  fontWeight: 600,
  textAlign: "right",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  minWidth: ELC_COL_W,
  boxShadow: TH_SH_ELC,
});
const tdBaseELC = (bg, align = "right", fw = 400, isFirst = false) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "7px 10px",
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
  ...(isFirst ? { borderLeft: "2px solid #C8D0DC" } : {}),
});
const tdFixedELC = (bg, col, fw = 400, isLast = false) => ({
  ...tdBaseELC(bg, "left", fw),
  position: "sticky",
  left: col.left,
  zIndex: Z_S,
  width: col.w,
  minWidth: col.w,
  maxWidth: col.w,
  padding: "7px 10px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});
const tdGroupFixedELC = (col, isLast = false) => ({
  ...tdFixedELC(GROUP_BG, col, 600, isLast),
  color: "#1E3A5F",
  borderBottom: "1px solid #DBEAFE",
});
const tdGroupScrollELC = () => ({
  ...tdBaseELC(GROUP_BG, "right", 500),
  color: "#1E3A5F",
  borderBottom: "1px solid #DBEAFE",
});
const TOTAL_TOP_ELC = GH_ELC + CH_ELC - 1;
const tdTotalFixedELC = (col) => ({
  ...tdFixedELC(TOTAL_BG, col, 700),
  color: TOTAL_FG,
  position: "sticky",
  top: TOTAL_TOP_ELC,
  zIndex: Z_S + 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)",
});
const tdTotalScrollELC = (isFirst = false) => ({
  ...tdBaseELC(TOTAL_BG, "right", 700, isFirst),
  color: TOTAL_FG,
  position: "sticky",
  top: TOTAL_TOP_ELC,
  zIndex: 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
});
const CARD_ELC = {
  background: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid rgba(0,0,0,0.08)",
  overflow: "hidden",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
};
const CHEAD_ELC = {
  padding: "18px 28px",
  borderBottom: "2px solid #F0F2F5",
  background: "#FFFFFF",
};
const CTITLE_ELC = {
  fontSize: 17,
  fontWeight: 800,
  color: "#111827",
  letterSpacing: "-0.02em",
  fontFamily: FONT_ELC,
  margin: 0,
};
const CMETA_ELC = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT_ELC,
  marginTop: 2,
};

function ChevronIconELC({ open }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
      style={{
        transition: "transform 0.18s ease",
        transform: open ? "rotate(90deg)" : "rotate(0deg)",
        flexShrink: 0,
        display: "inline-block",
      }}
    >
      <path
        d="M3 2l4 3-4 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function EmployeeLaborComparison({
  filters = {},
  downloadRef,
  setParentDownloading,
  onHeaderMonthChange,
}) {
  const classes = useStyles();

  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const fyYear = filters.year || new Date().getFullYear();

  /* Derive initial month from global monthName filter, fall back to "May" */
  const filterMonth =
    Array.isArray(filters.monthName) && filters.monthName.length > 0
      ? filters.monthName[filters.monthName.length - 1]
      : null;

  const [month, setMonth] = useState(filterMonth || null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  /* Seed the default month from the live API (instead of hardcoding a month or
     version) when the global monthName filter hasn't already picked one. */
  useEffect(() => {
    if (filterMonth) return;
    getIndirectVersionsAPI()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const version = data[0];
        if (!version) return null;
        return getIndirectLaborMonthsAPI({
          financialYear: String(new Date().getFullYear()),
          version,
        });
      })
      .then((res) => {
        const arr = res?.data?.actualsMonth;
        const latest = Array.isArray(arr) && arr.length ? arr[arr.length - 1] : "Jun";
        setMonth((prev) => prev ?? latest);
      })
      .catch(() => setMonth((prev) => prev ?? "Jun"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Comparison API state */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const normM = (m) =>
        m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase();
      const f = { monthName: [normM(month)] };
      if (filters.year) f.financialYear = String(filters.year);
      if (filters.monthName?.length)
        f.month = filters.monthName.map(
          (m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase(),
        );
      if (filters.quarter?.length) f.quarter = filters.quarter;
      if (filters.department?.length) f.department = filters.department;
      if (filters.subDepartment?.length) f.subDepartment = filters.subDepartment;
      if (filters.employeeName?.length) f.employeeName = filters.employeeName;
      if (version) f.version = version;
      const { blob, filename } = await downloadLaborComparisonAPI(f);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* silent */
    } finally {
      setParentDownloading?.(false);
    }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  /* Versions */
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");

  /* Pagination */
  const [typePage, setTypePage] = useState(1);
  const [typePageSize, setTypePageSize] = useState(10);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [detailSort, setDetailSort] = useState({ key: null, dir: "asc" });
  const toggleDetailSort = (key) =>
    setDetailSort((s) =>
      s.key === key
        ? s.dir === "asc"
          ? { key, dir: "desc" }
          : { key: null, dir: "asc" }
        : { key, dir: "asc" },
    );

  useEffect(() => {
    getIndirectVersionsAPI().then((res) => {
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setVersions(list);
    });
  }, []);

  /* Fetch comparison data whenever month, version, or filters change */
  useEffect(() => {
    if (month == null) return;
    setLoading(true);
    setError(null);
    const normM = (m) =>
      m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase();
    const f = { monthName: [normM(month)] };
    if (filters.year) f.financialYear = String(filters.year);
    if (filters.monthName?.length)
      f.month = filters.monthName.map(
        (m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase(),
      );
    if (filters.quarter?.length) f.quarter = filters.quarter;
    if (filters.department?.length) f.department = filters.department;
    if (filters.subDepartment?.length) f.subDepartment = filters.subDepartment;
    if (filters.employeeName?.length) f.employeeName = filters.employeeName;
    if (version) f.version = version;
    getIndirectLaborComparisonAPI(f)
      .then((res) => {
        const data = res?.data ?? res ?? {};
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setSummary(data.summary ?? null);
        setLoading(false);

        const apiActualsMonth = data.actualsMonth;
        onHeaderMonthChange?.(
          Array.isArray(apiActualsMonth) && apiActualsMonth.length
            ? apiActualsMonth[apiActualsMonth.length - 1]
            : null,
        );
      })
      .catch(() => {
        setError("Failed to load data.");
        setLoading(false);
      });
  }, [month, version, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (type) =>
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      n.has(type) ? n.delete(type) : n.add(type);
      return n;
    });

  const vc = (v) => ({
    color: v === 0 || v == null ? CELL_FG : v > 0 ? PL_POS_C : PL_NEG_C,
  });
  const fv = (v) => fmtI(v);

  /* ── Build type summary groups from API rows ── */
  const typeGroupMap = {};
  rows.forEach((row) => {
    const key = row.type || "Unknown";
    if (!typeGroupMap[key])
      typeGroupMap[key] = {
        label: key,
        mtdA: 0,
        mtdB: 0,
        ytdA: 0,
        ytdB: 0,
        fyA: 0,
        fyB: 0,
      };
    typeGroupMap[key].mtdA += row.mtdActuals || 0;
    typeGroupMap[key].mtdB += row.mtdBudget || 0;
    typeGroupMap[key].ytdA += row.ytdActuals || 0;
    typeGroupMap[key].ytdB += row.ytdBudget || 0;
    typeGroupMap[key].fyA += row.fyActuals || 0;
    typeGroupMap[key].fyB += row.fyBudget || 0;
  });
  const allTypeRows = Object.values(typeGroupMap);

  /* ── Build detail groups from API rows ── */
  const detailGroupMap = {};
  rows.forEach((row) => {
    const key = row.type || "Unknown";
    if (!detailGroupMap[key]) detailGroupMap[key] = [];
    detailGroupMap[key].push(row);
  });

  /* ── Cost type filter ── */
  const selectedTypes =
    Array.isArray(filters.costTypeIndirect) &&
    filters.costTypeIndirect.length > 0
      ? filters.costTypeIndirect
      : null;

  const filteredTypeRows = allTypeRows.filter(
    (r) => !selectedTypes || selectedTypes.includes(r.label),
  );
  const sortedTypeRows = filteredTypeRows;

  const filteredTotal = filteredTypeRows.reduce(
    (acc, r) => ({
      mtdA: acc.mtdA + r.mtdA,
      mtdB: acc.mtdB + r.mtdB,
      ytdA: acc.ytdA + r.ytdA,
      ytdB: acc.ytdB + r.ytdB,
      fyA: acc.fyA + r.fyA,
      fyB: acc.fyB + r.fyB,
    }),
    { mtdA: 0, mtdB: 0, ytdA: 0, ytdB: 0, fyA: 0, fyB: 0 },
  );

  const filteredDetailGroups = Object.entries(detailGroupMap).filter(
    ([type]) => !selectedTypes || selectedTypes.includes(type),
  );

  /* ── Flatten all filtered detail rows (for row-level pagination) ── */
  const allFilteredRows = filteredDetailGroups.flatMap(([type, typeRows]) =>
    typeRows.map((r) => ({ ...r, _type: type })),
  );

  const sortedDetailRows =
    detailSort.key === null
      ? allFilteredRows
      : [...allFilteredRows].sort((a, b) => {
          const mult = detailSort.dir === "asc" ? 1 : -1;
          const k = detailSort.key;
          if (k === "costType" || k === "employeeName" || k === "employeeId")
            return String(a[k] ?? "").localeCompare(String(b[k] ?? "")) * mult;
          if (k === "mtdV")
            return (
              ((a.mtdActuals || 0) -
                (a.mtdBudget || 0) -
                ((b.mtdActuals || 0) - (b.mtdBudget || 0))) *
              mult
            );
          if (k === "ytdV")
            return (
              ((a.ytdActuals || 0) -
                (a.ytdBudget || 0) -
                ((b.ytdActuals || 0) - (b.ytdBudget || 0))) *
              mult
            );
          if (k === "fyV")
            return (
              ((a.fyActuals || 0) -
                (a.fyBudget || 0) -
                ((b.fyActuals || 0) - (b.fyBudget || 0))) *
              mult
            );
          return ((a[k] ?? 0) - (b[k] ?? 0)) * mult;
        });

  /* ── Pagination logic ── */
  useEffect(() => {
    setTypePage(1);
  }, [filteredTypeRows.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDetailPage(1);
  }, [allFilteredRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const typeTotalPages = Math.max(
    1,
    Math.ceil(sortedTypeRows.length / typePageSize),
  );
  const typeSafePage = Math.min(typePage, typeTotalPages);
  const pagedTypeRows = sortedTypeRows.slice(
    (typeSafePage - 1) * typePageSize,
    typeSafePage * typePageSize,
  );
  const typePageNumbers = (() => {
    if (typeTotalPages <= 7)
      return Array.from({ length: typeTotalPages }, (_, i) => i + 1);
    const ps = new Set([
      1,
      typeTotalPages,
      typeSafePage,
      typeSafePage - 1,
      typeSafePage + 1,
      typeSafePage - 2,
      typeSafePage + 2,
    ]);
    return [...ps]
      .filter((p) => p >= 1 && p <= typeTotalPages)
      .sort((a, b) => a - b);
  })();

  /* Detail table paginates by individual rows, then groups the slice for rendering */
  const detailTotalPages = Math.max(
    1,
    Math.ceil(sortedDetailRows.length / detailPageSize),
  );
  const detailSafePage = Math.min(detailPage, detailTotalPages);
  const pagedFlatRows = sortedDetailRows.slice(
    (detailSafePage - 1) * detailPageSize,
    detailSafePage * detailPageSize,
  );
  const detailPageNumbers = (() => {
    if (detailTotalPages <= 7)
      return Array.from({ length: detailTotalPages }, (_, i) => i + 1);
    const ps = new Set([
      1,
      detailTotalPages,
      detailSafePage,
      detailSafePage - 1,
      detailSafePage + 1,
      detailSafePage - 2,
      detailSafePage + 2,
    ]);
    return [...ps]
      .filter((p) => p >= 1 && p <= detailTotalPages)
      .sort((a, b) => a - b);
  })();

  /* Re-group the paged flat rows to preserve group headers within the page */
  const pagedGroups = [];
  pagedFlatRows.forEach((row) => {
    const last = pagedGroups[pagedGroups.length - 1];
    if (!last || last.type !== row._type) {
      pagedGroups.push({ type: row._type, rows: [row] });
    } else {
      last.rows.push(row);
    }
  });

  const pgBtn = (active) => ({
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: `1px solid ${active ? TH_BG_BAND : "#E2E8F0"}`,
    borderRadius: 6,
    background: active ? TH_BG_BAND : "#fff",
    fontSize: 12,
    color: active ? "#fff" : TH_BG_BAND,
    cursor: "pointer",
    fontFamily: FONT_ELC,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    fontWeight: active ? 600 : 400,
  });

  const PaginationBar = ({
    total,
    pageSize,
    setPS,
    safePg,
    totalPgs,
    setPg,
    pageNums,
    font,
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 20px",
        borderTop: "1px solid #E2E8F0",
        background: "#FAFAFA",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: "#94A3B8",
          fontFamily: font,
          whiteSpace: "nowrap",
        }}
      >
        {total} row{total !== 1 ? "s" : ""}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 12,
            color: "#94A3B8",
            fontFamily: font,
            whiteSpace: "nowrap",
          }}
        >
          Rows Per Page
        </span>
        <select
          style={{
            height: 28,
            border: "1px solid #E2E8F0",
            borderRadius: 6,
            fontSize: 12,
            color: "#374151",
            padding: "0 6px",
            background: "#fff",
            cursor: "pointer",
            outline: "none",
            fontFamily: font,
          }}
          value={pageSize}
          onChange={(e) => {
            setPS(Number(e.target.value));
            setPg(1);
          }}
        >
          {[10, 25, 50, 100, ...(total > 100 ? [total] : [])].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        {totalPgs > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <button
              style={pgBtn(false)}
              disabled={safePg === 1}
              onClick={() => setPg(1)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="7,2 3,6 7,10" />
                <polyline points="11,2 7,6 11,10" />
              </svg>
            </button>
            <button
              style={pgBtn(false)}
              disabled={safePg === 1}
              onClick={() => setPg((p) => p - 1)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="8,2 4,6 8,10" />
              </svg>
            </button>
            {pageNums.map((p, i) => (
              <React.Fragment key={p}>
                {i > 0 && pageNums[i - 1] < p - 1 && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#94A3B8",
                      padding: "0 4px",
                      userSelect: "none",
                    }}
                  >
                    …
                  </span>
                )}
                <button style={pgBtn(p === safePg)} onClick={() => setPg(p)}>
                  {p}
                </button>
              </React.Fragment>
            ))}
            <button
              style={pgBtn(false)}
              disabled={safePg === totalPgs}
              onClick={() => setPg((p) => p + 1)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="4,2 8,6 4,10" />
              </svg>
            </button>
            <button
              style={pgBtn(false)}
              disabled={safePg === totalPgs}
              onClick={() => setPg(totalPgs)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="5,2 9,6 5,10" />
                <polyline points="1,2 5,6 1,10" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Actuals Selection filter ── */}
      <div className={classes.svbFilters} style={{ marginBottom: "14px" }}>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>Actuals Selection</span>
          <select
            className={classes.svbFilterSelect}
            value={month ?? ""}
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        {/*
        {versions.length > 0 && (
          <div className={classes.svbFilterGroup}>
            <span className={classes.svbFilterLabel}>Version</span>
            <select className={classes.svbFilterSelect} value={version} onChange={(e) => setVersion(e.target.value)}>
              <option value="">All</option>
              {versions.map((v) => (
                <option key={typeof v === "object" ? v.value ?? v.id : v} value={typeof v === "object" ? v.value ?? v.id : v}>
                  {typeof v === "object" ? v.label ?? v.name ?? v.value ?? v.id : v}
                </option>
              ))}
            </select>
          </div>
        )} */}
      </div>

      {loading || month == null ? (
        <Loading />
      ) : error ? (
        <div
          style={{
            padding: "8px 0 12px",
            fontFamily: FONT_ELC,
            fontSize: 13,
            color: "#DC2626",
          }}
        >
          {error}
        </div>
      ) : (
        <>
          {/* ── Type summary table ── */}
          <div style={CARD_ELC}>
            <div style={CHEAD_ELC}>
              <h2 style={CTITLE_ELC}>Type Summary</h2>
              <div style={CMETA_ELC}>
                Indirect Labor Vs Budget · MTD / YTD / FY-{fyYear}
              </div>
            </div>
            <div style={TABLE_SCROLL_WRAP_STYLE}>
              <table
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                  fontFamily: FONT_ELC,
                  fontSize: 12,
                  width: "100%",
                  minWidth: TYPE_W_ELC + ELC_COL_W * 9,
                }}
              >
                <colgroup>
                  <col style={{ width: TYPE_W_ELC }} />
                  {Array.from({ length: 9 }, (_, i) => (
                    <col key={i} style={{ width: ELC_COL_W }} />
                  ))}
                </colgroup>
                <thead style={{ background: TH_BG }}>
                  <tr style={{ height: GH_ELC }}>
                    <th
                      style={{
                        ...thCoreELC(
                          0,
                          GH_ELC + CH_ELC,
                          Z_H + 1,
                          "7px 14px",
                          HEADER_BG,
                        ),
                        left: 0,
                        width: TYPE_W_ELC,
                        minWidth: TYPE_W_ELC,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        textAlign: "left",
                        boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_ELC}`,
                        borderRight: "none",
                      }}
                      rowSpan={2}
                    >
                      Type
                    </th>
                    <th colSpan={3} style={thBandELC()}>
                      MTD {month}-{fyYear}
                    </th>
                    <th colSpan={3} style={thBandELC()}>
                      YTD {month}-{fyYear}
                    </th>
                    <th colSpan={3} style={thBandELC()}>
                      {month} Actuals + Fcst FY-{fyYear}
                    </th>
                  </tr>
                  <tr style={{ height: CH_ELC }}>
                    {[
                      "MTD Actuals",
                      "MTD Budget",
                      "MTD Variance",
                      "YTD Actuals",
                      "YTD Budget",
                      "YTD Variance",
                      `FY-${fyYear} Fcst`,
                      `FY-${fyYear} Budget`,
                      "FY Variance",
                    ].map((lbl, i) => (
                      <th key={i} style={thSubELC()}>
                        {lbl}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Total row */}
                  {(() => {
                    const r = filteredTotal;
                    return (
                      <tr style={{ height: RH_ELC }}>
                        <td
                          style={{
                            ...tdTotalFixedELC({ left: 0, w: TYPE_W_ELC }),
                            width: TYPE_W_ELC,
                          }}
                        >
                          Total
                        </td>
                        {[
                          { a: r.mtdA, b: r.mtdB },
                          { a: r.ytdA, b: r.ytdB },
                          { a: r.fyA, b: r.fyB },
                        ].flatMap(({ a, b }, gi) => {
                          const v = a - b;
                          return [
                            <td key={gi + "a"} style={tdTotalScrollELC(true)}>
                              {fv(a)}
                            </td>,
                            <td key={gi + "b"} style={tdTotalScrollELC()}>
                              {fv(b)}
                            </td>,
                            <td
                              key={gi + "v"}
                              style={{ ...tdTotalScrollELC(), ...vc(v) }}
                            >
                              {fv(v)}
                            </td>,
                          ];
                        })}
                      </tr>
                    );
                  })()}
                  {/* Data rows — filtered by costTypeIndirect */}
                  {sortedTypeRows.map((row, i) => {
                    const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
                    return (
                      <tr key={i} style={{ height: RH_ELC }}>
                        <td
                          style={{
                            ...tdFixedELC(bg, { left: 0, w: TYPE_W_ELC }),
                            width: TYPE_W_ELC,
                          }}
                        >
                          {row.label}
                        </td>
                        {[
                          { a: row.mtdA, b: row.mtdB },
                          { a: row.ytdA, b: row.ytdB },
                          { a: row.fyA, b: row.fyB },
                        ].flatMap(({ a, b }, gi) => {
                          const v = a - b;
                          return [
                            <td
                              key={gi + "a"}
                              style={tdBaseELC(bg, "right", 400, true)}
                            >
                              {fv(a)}
                            </td>,
                            <td key={gi + "b"} style={tdBaseELC(bg)}>
                              {fv(b)}
                            </td>,
                            <td
                              key={gi + "v"}
                              style={{ ...tdBaseELC(bg), ...vc(v) }}
                            >
                              {fv(v)}
                            </td>,
                          ];
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Employee Detail table (from comparison API) ── */}
          <div style={CARD_ELC}>
            <div style={CHEAD_ELC}>
              <h2 style={CTITLE_ELC}>Employee Detail</h2>
              <div style={CMETA_ELC}>
                Actuals Vs Budget by Employee · MTD / YTD / FY-{fyYear}
              </div>
            </div>
            <div style={TABLE_SCROLL_WRAP_STYLE}>
              <table
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  tableLayout: "fixed",
                  fontFamily: FONT_ELC,
                  fontSize: 12,
                  width: "100%",
                  minWidth: ELC_FIXED_W + ELC_COL_W * 9,
                }}
              >
                <colgroup>
                  {ELC_COLS.map((c) => (
                    <col key={c.key} style={{ width: c.w }} />
                  ))}
                  {Array.from({ length: 9 }, (_, i) => (
                    <col key={i} style={{ width: ELC_COL_W }} />
                  ))}
                </colgroup>
                <thead style={{ background: TH_BG }}>
                  <tr style={{ height: GH_ELC }}>
                    {ELC_COLS.map((c, ci) => {
                      const dk = ELC_DETAIL_KEY[c.key];
                      return (
                        <th
                          key={c.key}
                          title={
                            detailSort.key === dk
                              ? detailSort.dir === "asc"
                                ? "Sorted ascending"
                                : "Sorted descending"
                              : "Sort"
                          }
                          style={{
                            ...thFixedSpanELC(c, ci === ELC_COLS.length - 1),
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                          rowSpan={2}
                          onClick={() => toggleDetailSort(dk)}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            {c.label}
                            <span
                              style={{
                                fontSize: 9,
                                color:
                                  detailSort.key === dk
                                    ? "#A5B4FC"
                                    : "rgba(255,255,255,0.45)",
                              }}
                            >
                              {detailSort.key === dk
                                ? detailSort.dir === "asc"
                                  ? "↑"
                                  : "↓"
                                : "↕"}
                            </span>
                          </span>
                        </th>
                      );
                    })}
                    <th colSpan={3} style={thBandELC()}>
                      MTD {month}-{fyYear}
                    </th>
                    <th colSpan={3} style={thBandELC()}>
                      YTD {month}-{fyYear}
                    </th>
                    <th colSpan={3} style={thBandELC()}>
                      {month} Actuals + Fcst FY-{fyYear}
                    </th>
                  </tr>
                  <tr style={{ height: CH_ELC }}>
                    {[
                      ["mtdActuals", "MTD Actuals"],
                      ["mtdBudget", "MTD Budget"],
                      ["mtdV", "MTD Variance"],
                      ["ytdActuals", "YTD Actuals"],
                      ["ytdBudget", "YTD Budget"],
                      ["ytdV", "YTD Variance"],
                      ["fyActuals", `FY-${fyYear} Fcst`],
                      ["fyBudget", `FY-${fyYear} Budget`],
                      ["fyV", "FY Variance"],
                    ].map(([k, lbl]) => (
                      <th
                        key={k}
                        title={
                          detailSort.key === k
                            ? detailSort.dir === "asc"
                              ? "Sorted ascending"
                              : "Sorted descending"
                            : "Sort"
                        }
                        style={{
                          ...thSubELC(),
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        onClick={() => toggleDetailSort(k)}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 3,
                            width: "100%",
                          }}
                        >
                          {lbl}
                          <span
                            style={{
                              fontSize: 9,
                              color:
                                detailSort.key === k
                                  ? "#A5B4FC"
                                  : "rgba(255,255,255,0.4)",
                            }}
                          >
                            {detailSort.key === k
                              ? detailSort.dir === "asc"
                                ? "↑"
                                : "↓"
                              : "↕"}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Grand total row */}
                  {(() => {
                    const r = filteredTotal;
                    return (
                      <tr style={{ height: RH_ELC }}>
                        {ELC_COLS.map((c, ci) => (
                          <td key={c.key} style={tdTotalFixedELC(c)}>
                            {ci === 0 ? "Total" : ""}
                          </td>
                        ))}
                        {[
                          { a: r.mtdA, b: r.mtdB },
                          { a: r.ytdA, b: r.ytdB },
                          { a: r.fyA, b: r.fyB },
                        ].flatMap(({ a, b }, gi) => {
                          const v = a - b;
                          return [
                            <td key={gi + "a"} style={tdTotalScrollELC(true)}>
                              {fv(a)}
                            </td>,
                            <td key={gi + "b"} style={tdTotalScrollELC()}>
                              {fv(b)}
                            </td>,
                            <td
                              key={gi + "v"}
                              style={{ ...tdTotalScrollELC(), ...vc(v) }}
                            >
                              {fv(v)}
                            </td>,
                          ];
                        })}
                      </tr>
                    );
                  })()}
                  {/* Dynamic groups — re-grouped from paginated flat rows */}
                  {pagedGroups.map(({ type, rows: typeDetailRows }) => {
                    const grpTotals = typeGroupMap[type] || {
                      mtdA: 0,
                      mtdB: 0,
                      ytdA: 0,
                      ytdB: 0,
                      fyA: 0,
                      fyB: 0,
                    };
                    const isGroupOpen = !collapsedGroups.has(type);
                    return (
                      <React.Fragment key={type}>
                        <tr
                          style={{ height: RH_ELC, cursor: "pointer" }}
                          onClick={() => toggleGroup(type)}
                        >
                          {ELC_COLS.map((c, ci) => (
                            <td
                              key={c.key}
                              style={tdGroupFixedELC(
                                c,
                                ci === ELC_COLS.length - 1,
                              )}
                            >
                              {ci === 0 && (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    color: "#1E3A5F",
                                  }}
                                >
                                  <ChevronIconELC open={isGroupOpen} />
                                  <strong>{type}</strong>
                                </span>
                              )}
                            </td>
                          ))}
                          {[
                            { a: grpTotals.mtdA, b: grpTotals.mtdB },
                            { a: grpTotals.ytdA, b: grpTotals.ytdB },
                            { a: grpTotals.fyA, b: grpTotals.fyB },
                          ].flatMap(({ a, b }, gi) => {
                            const v = a - b;
                            return [
                              <td
                                key={gi + "a"}
                                style={{
                                  ...tdGroupScrollELC(),
                                  borderLeft: "2px solid #C8D0DC",
                                }}
                              >
                                {fv(a)}
                              </td>,
                              <td key={gi + "b"} style={tdGroupScrollELC()}>
                                {fv(b)}
                              </td>,
                              <td
                                key={gi + "v"}
                                style={{ ...tdGroupScrollELC(), ...vc(v) }}
                              >
                                {fv(v)}
                              </td>,
                            ];
                          })}
                        </tr>
                        {isGroupOpen &&
                          typeDetailRows.map((row, i) => {
                            const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
                            return (
                              <tr key={i} style={{ height: RH_ELC }}>
                                <td
                                  style={tdFixedELC(bg, ELC_COLS[0])}
                                  title={row.costType || ""}
                                >
                                  {row.costType || ""}
                                </td>

                                <td
                                  style={tdFixedELC(bg, ELC_COLS[1])}
                                  title={row.employeeName || ""}
                                >
                                  {row.employeeName || ""}
                                </td>
                                <td
                                  style={tdFixedELC(bg, ELC_COLS[2], 400, true)}
                                  title={row.employeeId || ""}
                                >
                                  {row.employeeId || ""}
                                </td>
                                {[
                                  { a: row.mtdActuals, b: row.mtdBudget },
                                  { a: row.ytdActuals, b: row.ytdBudget },
                                  { a: row.fyActuals, b: row.fyBudget },
                                ].flatMap(({ a, b }, gi) => {
                                  const v = (a || 0) - (b || 0);
                                  return [
                                    <td
                                      key={gi + "a"}
                                      style={tdBaseELC(bg, "right", 400, true)}
                                    >
                                      {fv(a || 0)}
                                    </td>,
                                    <td key={gi + "b"} style={tdBaseELC(bg)}>
                                      {fv(b || 0)}
                                    </td>,
                                    <td
                                      key={gi + "v"}
                                      style={{ ...tdBaseELC(bg), ...vc(v) }}
                                    >
                                      {fv(v)}
                                    </td>,
                                  ];
                                })}
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar
              total={allFilteredRows.length}
              pageSize={detailPageSize}
              setPS={setDetailPageSize}
              safePg={detailSafePage}
              totalPgs={detailTotalPages}
              setPg={setDetailPage}
              pageNums={detailPageNumbers}
              font={FONT_ELC}
            />
          </div>
        </>
      )}
    </div>
  );
}
